import { PrimaryButton } from '@components/Buttons/PrimaryButton';
import { ModalWindow } from '@components/Modal/ModalWindow';
import { ModalWindowActions } from '@components/Modal/ModalWindowActions';
import { ModalWindowContent } from '@components/Modal/ModalWindowContent';
import { ModalWindowTitle } from '@components/Modal/ModalWindowTitle';
import { useField } from '@hooks/useField';
import { useForm } from '@hooks/useForm';
import useLoading from '@hooks/useLoading';
import useMessage from '@hooks/useMessage';
import { IModalWindow } from '@hooks/useModalWindow';
import { useValidation } from '@hooks/useValidation';
import { IVendedor } from '@model/Vendedor';
import { IVendedorRegistrar, useVendedorService } from '@services/VendedorService';
import { yup } from '@validations/Yup';
import { AxiosResponse } from 'axios';
import React from 'react';
import { VendedorRegistrarForm } from './VendedorRegistrarForm';

export type VendedorRegistrarModalProps = {
    modal: IModalWindow
    onVendedorRegistrado: (vendedor: AxiosResponse<IVendedor>) => void
}

export function VendedorRegistrarModal ({ modal, onVendedorRegistrado }: VendedorRegistrarModalProps) {
    const loading = useLoading();
    const message = useMessage();

    const form = useForm<IVendedorRegistrar>({
        nome: useField(),
        cpf: useField(),
        telefone: useField()
    })

    const validation = useValidation<IVendedorRegistrar>({
        nome: yup.string().trim().required(),
        cpf: yup.string().trim().required().validCPF(),
    });

    const vendedorService = useVendedorService();

    const handleConfirm = React.useCallback(() => {
        if (!loading.isLoading) {
            const values = form.getInputValues();

            form.clearErrors();

            validation.validate(values)
                .then(() => {
                    loading.start();
                    vendedorService.registrar(values)
                        .then(response => {
                            message.success("Vendedor criado com sucesso");
                            modal.close();
                            onVendedorRegistrado(response);
                        })
                        .catch(e => {
                            message.handleResponse(e)
                                .badRequest(form.setErrorsFromServer)
                                .showException();
                        })
                        .finally(loading.stop);
                })
                .catch(form.setErrorsFromYup);
        }
    }, [form, loading, message, modal, onVendedorRegistrado, vendedorService, validation]);

    const handleEnter = React.useCallback(() => {
        form.reset();
    }, [form]);

    return (
        <ModalWindow
            modal={modal}
            disabled={loading.isLoading}
            size="sm"
            TransitionProps={{
                onEnter: handleEnter
            }}
        >
            <ModalWindowTitle 
                title={`Registro de Novo Vendedor`}
            />

            <ModalWindowContent>
                <VendedorRegistrarForm 
                    form={form}
                />
            </ModalWindowContent>

            <ModalWindowActions>
                <PrimaryButton onClick={handleConfirm}>Registrar Vendedor</PrimaryButton>
            </ModalWindowActions>
        </ModalWindow>
    )
}